import { staticCachedAxios, noCacheAxios, invalidateCache } from '../../../shared/config/cachedAxiosConfig';
import { getAuthHeaders } from '../../../shared/services/auth/authHeaders';
import { tokenService } from '../../../shared/services/auth/tokenService';
import { DIVISION_ENDPOINTS } from '../config/organizationApiEndpoints';
import { PERFORMANCE_CONFIG } from '../config/performanceConfig';
import logger from '../../../shared/services/logging/logger';
import { validateTenantId, validateDivisionId, validateNonEmptyString } from '../utils/validators';

// Fired whenever a user's division membership changes (added/removed/role changed on any
// division, by anyone). useUserDivisions() listens for this — it otherwise fetches once on
// mount with no cache and no way to learn about mutations made elsewhere, so without this
// event the division switcher stays stuck showing pre-mutation state until a full reload.
const notifyUserDivisionsChanged = () => {
  try {
    window.dispatchEvent(new CustomEvent('userDivisionsChanged'));
  } catch (error) {
    // best-effort — a missed refresh event isn't worth failing the mutation over
  }
};

export const DivisionService = {
  getOrganizationDivisions: async (tenantId) => {
    validateTenantId(tenantId);

    try {
      // FIXED: Await the async getAuthHeaders function
      const headers = await getAuthHeaders();
      
      const response = await staticCachedAxios.get(DIVISION_ENDPOINTS.byOrganization(tenantId), { headers });
      return response.data;
    } catch (error) {
      logger.error('Failed to get organization divisions', { method: 'getOrganizationDivisions', error: error.message, tenantId });
      throw error;
    }
  },

  createDivision: async (divisionData) => {
    validateTenantId(divisionData.tenantId);
    validateNonEmptyString(divisionData.name, 'Division name');

    try {
      // FIXED: Await the async getAuthHeaders function
      const headers = await getAuthHeaders();

      // Prepare the payload with description support
      const payload = {
        name: divisionData.name,
        tenantId: divisionData.tenantId,
        ...(divisionData.description && { description: divisionData.description })
      };

      const response = await noCacheAxios.post(DIVISION_ENDPOINTS.create, payload, { headers });
      invalidateCache(staticCachedAxios);
      return response.data;
    } catch (error) {
      logger.error('Failed to create division', { method: 'createDivision', error: error.message, divisionData });
      throw error;
    }
  },

  updateDivision: async (divId, divisionData) => {
    validateDivisionId(divId);
    validateTenantId(divisionData.tenantId);
    validateNonEmptyString(divisionData.name, 'Division name');

    try {
      // FIXED: Await the async getAuthHeaders function
      const headers = await getAuthHeaders();

      // Prepare the payload with description support (same DivisionRegistrationDto as create)
      const payload = {
        name: divisionData.name,
        tenantId: divisionData.tenantId,
        ...(divisionData.description !== undefined && { description: divisionData.description })
      };

      const response = await noCacheAxios.put(DIVISION_ENDPOINTS.update(divId), payload, { headers });
      invalidateCache(staticCachedAxios);
      return response.data;
    } catch (error) {
      logger.error('Failed to update division', { method: 'updateDivision', error: error.message, divId });
      throw error;
    }
  },

  // Soft-delete only — the backend sets status to DELETED and keeps the row (and every
  // Membership) intact, so this is always recoverable via restoreDivision(). It rejects a
  // tenant's default division with a 400 ("Cannot delete a Tenant's default Division").
  deleteDivision: async (divId) => {
    validateDivisionId(divId);

    try {
      // FIXED: Await the async getAuthHeaders function
      const headers = await getAuthHeaders();

      const response = await noCacheAxios.delete(DIVISION_ENDPOINTS.delete(divId), { headers });
      invalidateCache(staticCachedAxios);
      // Deletion re-points/clears divisionId for anyone whose current division this was —
      // affects their switcher options.
      notifyUserDivisionsChanged();
      return response.data;
    } catch (error) {
      logger.error('Failed to delete division', { method: 'deleteDivision', error: error.message, divId });
      throw error;
    }
  },

  // Restores a soft-deleted division back to ACTIVE. Does NOT re-point any user's current
  // divisionId back to it — members who still have a Membership row simply see it reappear
  // in their division list and can switch back in themselves.
  restoreDivision: async (divId) => {
    validateDivisionId(divId);

    try {
      const headers = await getAuthHeaders();

      const response = await noCacheAxios.post(DIVISION_ENDPOINTS.restore(divId), {}, { headers });
      invalidateCache(staticCachedAxios);
      notifyUserDivisionsChanged();
      return response.data;
    } catch (error) {
      logger.error('Failed to restore division', { method: 'restoreDivision', error: error.message, divId });
      throw error;
    }
  },

  getDivisionMembers: async (divId) => {
    validateDivisionId(divId);

    try {
      // FIXED: Await the async getAuthHeaders function
      const headers = await getAuthHeaders();

      const response = await staticCachedAxios.get(DIVISION_ENDPOINTS.members(divId), { headers });
      return response.data;
    } catch (error) {
      logger.warn('Failed to get division members, returning empty array', { method: 'getDivisionMembers', error: error.message, divId });
      return [];
    }
  },

  /**
   * Fetch a single division's own details (name, description, createdDate, etc.)
   * scoped to just that division — usable by a plain Division Admin, unlike
   * getOrganizationDivisions()/byOrganization, which is Tenant-Admin-only.
   */
  getDivisionDetails: async (divId) => {
    validateDivisionId(divId);

    try {
      const headers = await getAuthHeaders();

      const response = await staticCachedAxios.get(DIVISION_ENDPOINTS.details(divId), { headers });
      return response.data;
    } catch (error) {
      logger.error('Failed to get division details', { method: 'getDivisionDetails', error: error.message, divId });
      throw error;
    }
  },

  /**
   * Fetch divisions with members using optimized strategy
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<Array>} Divisions with members embedded
   */
  getOrganizationDivisionsWithMembers: async (tenantId) => {
    validateTenantId(tenantId);

    const startTime = Date.now();

    try {
      const headers = await getAuthHeaders();

      // TODO: When backend supports it, use includeMembers query parameter
      // const response = await staticCachedAxios.get(
      //   `${DIVISION_ENDPOINTS.byOrganization(tenantId)}?includeMembers=true`,
      //   { headers }
      // );
      // return response.data;

      // Current implementation: Fetch divisions first, then members in parallel
      const divisionsData = await staticCachedAxios.get(DIVISION_ENDPOINTS.byOrganization(tenantId), { headers });

      if (!divisionsData.data || divisionsData.data.length === 0) {
        return [];
      }

      // Batch member fetches with concurrency limit
      const divisions = divisionsData.data;
      const batchSize = PERFORMANCE_CONFIG.MAX_PARALLEL_MEMBER_FETCHES;
      const divisionsWithMembers = [];

      for (let i = 0; i < divisions.length; i += batchSize) {
        const batch = divisions.slice(i, i + batchSize);
        const batchResults = await Promise.all(
          batch.map(async (division) => {
            try {
              const members = await DivisionService.getDivisionMembers(division.divisionId);
              return { ...division, members: members || [] };
            } catch (error) {
              logger.warn('Failed to load division members in batch', {
                error: error.message,
                divisionId: division.divisionId
              });
              return { ...division, members: [] };
            }
          })
        );
        divisionsWithMembers.push(...batchResults);
      }

      const duration = Date.now() - startTime;
      if (PERFORMANCE_CONFIG.LOG_MEMBER_FETCH_PERFORMANCE) {
        logger.info('Division member fetch completed', {
          tenantId,
          divisionCount: divisions.length,
          apiCalls: divisions.length + 1,
          duration: `${duration}ms`,
          mode: 'parallel-batched'
        });
      }

      return divisionsWithMembers;
    } catch (error) {
      logger.error('Failed to get divisions with members', {
        method: 'getOrganizationDivisionsWithMembers',
        error: error.message,
        tenantId
      });
      throw error;
    }
  },

  getUserDivisions: async (userId) => {
    validateNonEmptyString(userId, 'User ID');

    try {
      const headers = await getAuthHeaders();
      const response = await staticCachedAxios.get(DIVISION_ENDPOINTS.forUser(userId), { headers });
      return response.data;
    } catch (error) {
      logger.error('Failed to get user divisions', { method: 'getUserDivisions', error: error.message, userId });
      throw error;
    }
  },

  switchDivision: async (divId) => {
    validateDivisionId(divId);

    const refreshToken = tokenService.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available — please log in again');
    }

    try {
      const headers = await getAuthHeaders();
      const response = await noCacheAxios.post(
        DIVISION_ENDPOINTS.switchDivision(divId),
        { refreshToken },
        { headers }
      );
      invalidateCache(staticCachedAxios);
      return response.data;
    } catch (error) {
      logger.error('Failed to switch division', { method: 'switchDivision', error: error.message, divId });
      throw error;
    }
  },

  addMemberToDivision: async (divId, userId, role) => {
    validateDivisionId(divId);

    try {
      const headers = await getAuthHeaders();
      const response = await noCacheAxios.post(DIVISION_ENDPOINTS.members(divId), { userId, role }, { headers });
      invalidateCache(staticCachedAxios);
      notifyUserDivisionsChanged();
      return response.data;
    } catch (error) {
      logger.error('Failed to add member to division', { method: 'addMemberToDivision', error: error.message, divId, userId, role });
      throw error;
    }
  },

  removeMemberFromDivision: async (divId, userId) => {
    validateDivisionId(divId);

    try {
      const headers = await getAuthHeaders();
      const response = await noCacheAxios.delete(`${DIVISION_ENDPOINTS.members(divId)}/${userId}`, { headers });
      invalidateCache(staticCachedAxios);
      notifyUserDivisionsChanged();
      return response.data;
    } catch (error) {
      logger.error('Failed to remove member from division', { method: 'removeMemberFromDivision', error: error.message, divId, userId });
      throw error;
    }
  },

  updateDivisionMemberRole: async (divId, userId, role) => {
    validateDivisionId(divId);

    try {
      const headers = await getAuthHeaders();
      const response = await noCacheAxios.put(`${DIVISION_ENDPOINTS.members(divId)}/${userId}/role`, { role }, { headers });
      invalidateCache(staticCachedAxios);
      notifyUserDivisionsChanged();
      return response.data;
    } catch (error) {
      logger.error('Failed to update division member role', { method: 'updateDivisionMemberRole', error: error.message, divId, userId, role });
      throw error;
    }
  },

  /**
   * FUTURE: Batch fetch members for multiple divisions in single API call
   * This method stub is ready for when backend implements batch endpoint
   *
   * Expected backend endpoint: POST /divisions/batch/members
   * Expected payload: { divisionIds: string[] }
   * Expected response: { [divisionId: string]: User[] }
   *
   * @param {string[]} divisionIds - Array of division IDs
   * @returns {Promise<Object>} Map of division ID to members array
   */
  getBatchDivisionMembers: async (divisionIds) => {
    // TODO: Implement when backend supports batch endpoint
    throw new Error('Batch division members endpoint not yet implemented in backend');

    /* Future implementation:
    const headers = await getAuthHeaders();
    const response = await staticCachedAxios.post(
      `${API_BASE_URL}/divisions/batch/members`,
      { divisionIds },
      { headers }
    );
    return response.data;
    */
  }
};